import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { trackPageview, initPageviewTracker } from "@/lib/pageview-tracker";

function PageviewTracker() {
  const location = useLocation();
  useEffect(() => { initPageviewTracker(); }, []);
  useEffect(() => { trackPageview(location.pathname); }, [location.pathname]);
  return null;
}

const Index = lazy(() => import("./pages/Index"));
const Gallery = lazy(() => import("./pages/Gallery"));
const LocalSeoPage = lazy(() => import("./pages/LocalSeoPage"));
const ProjectDiscovery = lazy(() => import("./pages/ProjectDiscovery"));
const QuoteConfirmation = lazy(() => import("./pages/QuoteConfirmation"));
const CareersDiscovery = lazy(() => import("./pages/CareersDiscovery"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Gone = lazy(() => import("./pages/Gone"));
import { isLegacyGonePath } from "./pages/Gone";

function RootBlogRoute() {
  const { rootBlogSlug } = useParams<{ rootBlogSlug: string }>();
  return rootBlogSlug && /^blog-.+\.html$/.test(rootBlogSlug) ? <BlogPost /> : <NotFound />;
}

function CatchAllRoute() {
  const location = useLocation();
  return isLegacyGonePath(location.pathname) ? <Gone /> : <NotFound />;
}

// Portal pages
const PortalLogin = lazy(() => import("./pages/portal/Login"));
const ResetPassword = lazy(() => import("./pages/portal/ResetPassword"));
const PortalLayout = lazy(() => import("./components/portal/PortalLayout"));
const ProtectedRoute = lazy(() => import("./components/portal/ProtectedRoute"));

// Admin pages
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const AdminResetPassword = lazy(() => import("./pages/admin/ResetPassword"));
const AdminProtectedRoute = lazy(() => import("./components/admin/AdminProtectedRoute"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminQuoteRequests = lazy(() => import("./pages/admin/QuoteRequests"));
const AdminCareerInquiries = lazy(() => import("./pages/admin/CareerInquiries"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminProjects = lazy(() => import("./pages/admin/Projects"));
const AdminBlog = lazy(() => import("./pages/admin/Blog"));
const AdminBlogEditor = lazy(() => import("./pages/admin/BlogEditor"));
const Dashboard = lazy(() => import("./pages/portal/Dashboard"));
const Projects = lazy(() => import("./pages/portal/Projects"));
const ProjectDetail = lazy(() => import("./pages/portal/ProjectDetail"));
const Financials = lazy(() => import("./pages/portal/Financials"));
const Selections = lazy(() => import("./pages/portal/Selections"));
const Documents = lazy(() => import("./pages/portal/Documents"));
const Messages = lazy(() => import("./pages/portal/Messages"));
const Profile = lazy(() => import("./pages/portal/Profile"));
const Security = lazy(() => import("./pages/portal/Security"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <PageviewTracker />
          <Suspense fallback={null}>
            <Routes>
              {/* Public marketing site */}
              <Route path="/" element={<Index />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/electrician-north-vancouver" element={<LocalSeoPage slug="electrician-north-vancouver" />} />
              <Route path="/commercial-electrical-contractor-vancouver" element={<LocalSeoPage slug="commercial-electrical-contractor-vancouver" />} />
              <Route path="/ev-charger-installation-vancouver" element={<LocalSeoPage slug="ev-charger-installation-vancouver" />} />
              <Route path="/residential-electrician-vancouver" element={<LocalSeoPage slug="residential-electrician-vancouver" />} />
              <Route path="/electrical-service-upgrades-vancouver" element={<LocalSeoPage slug="electrical-service-upgrades-vancouver" />} />
              <Route path="/fire-alarm-installation-vancouver" element={<LocalSeoPage slug="fire-alarm-installation-vancouver" />} />
              <Route path="/low-voltage-cabling-vancouver" element={<LocalSeoPage slug="low-voltage-cabling-vancouver" />} />
              <Route path="/smart-home-automation-vancouver" element={<LocalSeoPage slug="smart-home-automation-vancouver" />} />
              <Route path="/project-discovery" element={<ProjectDiscovery />} />
              <Route path="/quote-confirmation" element={<QuoteConfirmation />} />
              <Route path="/careers-discovery" element={<CareersDiscovery />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/:rootBlogSlug" element={<RootBlogRoute />} />

              {/* Portal public routes */}
              <Route path="/portal/login" element={<PortalLogin />} />
              <Route path="/portal/reset-password" element={<ResetPassword />} />

              {/* Portal protected routes */}
              <Route
                path="/portal"
                element={
                  <ProtectedRoute>
                    <PortalLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="projects" element={<Projects />} />
                <Route path="projects/:id" element={<ProjectDetail />} />
                <Route path="financials" element={<Financials />} />
                <Route path="selections" element={<Selections />} />
                <Route path="documents" element={<Documents />} />
                <Route path="messages" element={<Messages />} />
                <Route path="profile" element={<Profile />} />
                <Route path="security" element={<Security />} />
              </Route>

              {/* Admin routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/reset-password" element={<AdminResetPassword />} />
              <Route
                path="/admin"
                element={
                  <AdminProtectedRoute>
                    <AdminLayout />
                  </AdminProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="projects" element={<AdminProjects />} />
                <Route path="blog" element={<AdminBlog />} />
                <Route path="blog/new" element={<AdminBlogEditor />} />
                <Route path="blog/:id/edit" element={<AdminBlogEditor />} />
                <Route path="quotes" element={<AdminQuoteRequests />} />
                <Route path="careers" element={<AdminCareerInquiries />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>

              {/* Legacy Joomla URL redirects → homepage (preserve visitors) */}
              <Route path="/index.php" element={<Navigate to="/" replace />} />
              <Route path="/author/*" element={<Navigate to="/" replace />} />
              <Route path="/category/*" element={<Navigate to="/" replace />} />

              <Route path="*" element={<CatchAllRoute />} />

            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
